import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Node } from 'reactflow';
import { FiGitMerge, FiX, FiChevronRight } from 'react-icons/fi';

// Couches Médaillon — détection par nom de workspace
const MEDALLION = [
  { id: 'bronze',     label: 'Bronze',     color: '#92400E', bg: '#FEF3C7', border: '#F59E0B', keywords: ['bronze', 'raw', 'ingest', 'landing', 'source'] },
  { id: 'silver',     label: 'Silver',     color: '#1E3A5F', bg: '#DBEAFE', border: '#3B82F6', keywords: ['silver', 'clean', 'refined', 'transform', 'staging', 'process'] },
  { id: 'gold',       label: 'Gold',       color: '#78350F', bg: '#FEF9EE', border: '#D97706', keywords: ['gold', 'serve', 'agg', 'curated', 'business', 'mart'] },
  { id: 'reporting',  label: 'Reporting',  color: '#1E40AF', bg: '#EFF6FF', border: '#2563EB', keywords: ['report', 'bi', 'viz', 'dashboard', 'publish', 'presentation'] },
  { id: 'ai',         label: 'IA & ML',    color: '#5B21B6', bg: '#F5F3FF', border: '#7C3AED', keywords: ['ai', 'ml', 'machine', 'science'] },
  { id: 'realtime',   label: 'Temps réel', color: '#065F46', bg: '#ECFDF5', border: '#059669', keywords: ['realtime', 'real-time', 'kql', 'eventhouse', 'streaming', 'event'] },
  { id: 'governance', label: 'Gouvernance',color: '#1F2937', bg: '#F9FAFB', border: '#6B7280', keywords: ['governance', 'purview', 'compliance', 'domain'] },
  { id: 'other',      label: 'Autre',      color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', keywords: [] },
] as const;

const FABRIC_ITEM_TYPES = new Set([
  'fabric_eventstream', 'fabric_pipeline', 'fabric_dataflow',
  'fabric_lakehouse', 'fabric_onelake', 'fabric_sql_database',
  'fabric_notebook', 'fabric_warehouse', 'fabric_semantic_model',
  'fabric_report', 'fabric_ml_model', 'fabric_data_agent',
  'fabric_eventhouse', 'fabric_kql_database',
  'azurerm_purview_account', 'azurerm_policy_assignment', 'fabric_domain',
]);

function detectMedallionLayer(wsLabel: string): typeof MEDALLION[number] {
  const name = wsLabel.toLowerCase();
  for (const m of MEDALLION) {
    if (m.keywords.some(k => name.includes(k))) return m;
  }
  return MEDALLION[MEDALLION.length - 1]; // 'other'
}

interface WorkspaceSection {
  wsId: string;
  wsLabel: string;
  layer: typeof MEDALLION[number];
  items: Node[];
}

const FABRIC_ICONS: Record<string, string> = {
  fabric_eventstream:        '/icons/fabric/eventstream_64_item.svg',
  fabric_pipeline:           '/icons/fabric/pipeline_64_item.svg',
  fabric_dataflow:           '/icons/fabric/dataflow_gen2_64_item.svg',
  fabric_lakehouse:          '/icons/fabric/lakehouse_64_item.svg',
  fabric_onelake:            '/icons/fabric/one_lake_48_color.svg',
  fabric_sql_database:       '/icons/fabric/sql_database_64_item.svg',
  fabric_notebook:           '/icons/fabric/notebook_64_item.svg',
  fabric_warehouse:          '/icons/fabric/data_warehouse_64_item.svg',
  fabric_semantic_model:     '/icons/fabric/semantic_model_64_item.svg',
  fabric_report:             '/icons/fabric/power_bi_48_color.svg',
  fabric_ml_model:           '/icons/fabric/data_science_48_color.svg',
  fabric_data_agent:         '/icons/fabric/data_agent_64_item.svg',
  fabric_eventhouse:         '/icons/fabric/event_house_64_item.svg',
  fabric_kql_database:       '/icons/fabric/kql_database_64_item.svg',
  azurerm_purview_account:   '/icons/fabric/purview_48_color.svg',
  azurerm_policy_assignment: '/icons/azure/10316-icon-service-Policy.svg',
  fabric_domain:             '/icons/azure/10007-icon-service-Resource-Groups.svg',
};

interface FabricLineagePanelProps {
  nodes: Node[];
  onClose: () => void;
  onSelectNode?: (nodeId: string) => void;
}

const FabricLineagePanel: React.FC<FabricLineagePanelProps> = ({ nodes, onClose, onSelectNode }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const saved = localStorage.getItem('lineagePanel.position');
    return saved ? JSON.parse(saved) : { x: 16, y: 80 };
  });
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);

  useEffect(() => {
    localStorage.setItem('lineagePanel.position', JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging || !dragRef.current) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 360, dragRef.current.posX + (e.clientX - dragRef.current.startX))),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.posY + (e.clientY - dragRef.current.startY))),
      });
    };
    const onUp = () => { setIsDragging(false); dragRef.current = null; };
    if (isDragging) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [isDragging]);

  // Workspace-first classification: each workspace gets a section ordered by Medallion layer
  const workspaceSections = useMemo((): WorkspaceSection[] => {
    const wsNodes = nodes.filter(n => n.data?.type === 'fabric_workspace');
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    const sections: WorkspaceSection[] = wsNodes.map(ws => {
      const wsLabel = ws.data?.label || ws.data?.params?.display_name || ws.data?.params?.name || ws.id;
      const layer = detectMedallionLayer(wsLabel);

      const items = nodes.filter(n => {
        const t = n.data?.type || '';
        if (!FABRIC_ITEM_TYPES.has(t)) return false;
        // Nested inside workspace via React Flow parentNode (template path)
        if (n.parentNode === ws.id) return true;
        // Item inside a folder which is itself inside this workspace
        if (n.parentNode) {
          const parent = nodeMap.get(n.parentNode);
          if (parent?.parentNode === ws.id) return true;
        }
        // Associated via workspaceId param (sidebar-dropped nodes)
        if (n.data?.params?.workspaceId && n.data.params.workspaceId === ws.id) return true;
        return false;
      });

      return { wsId: ws.id, wsLabel, layer, items };
    });

    const layerOrder = MEDALLION.map(m => m.id);
    return sections.sort((a, b) => layerOrder.indexOf(a.layer.id) - layerOrder.indexOf(b.layer.id));
  }, [nodes]);

  const fabricCapacities = nodes.filter(n => n.data?.type === 'fabric_capacity');
  const totalItems = workspaceSections.reduce((s, ws) => s + ws.items.length, 0);
  const hasFabric = fabricCapacities.length > 0 || workspaceSections.length > 0;

  return (
    <div
      style={{
        position: 'fixed', left: position.x, top: position.y,
        width: 340, background: 'white',
        border: '1px solid #E5E7EB', borderRadius: 10,
        boxShadow: '0 4px 24px rgba(0,0,0,0.13)', zIndex: 1200,
        userSelect: isDragging ? 'none' : 'auto',
      }}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('.lp-drag')) {
          setIsDragging(true);
          dragRef.current = { startX: e.clientX, startY: e.clientY, posX: position.x, posY: position.y };
          e.preventDefault();
        }
      }}
    >
      {/* Header */}
      <div className="lp-drag" style={{
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)',
        padding: '10px 14px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', cursor: 'grab', borderRadius: '10px 10px 0 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiGitMerge size={16} color="white" />
          <span style={{ color: 'white', fontWeight: 700, fontSize: 13 }}>Vue Lineage — Flux de données</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: 2 }}>
          <FiX size={16} />
        </button>
      </div>

      <div style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', padding: 12 }}>
        {!hasFabric && (
          <div style={{ textAlign: 'center', padding: '24px 16px', color: '#9CA3AF', fontSize: 12 }}>
            <FiGitMerge size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>Aucun workspace Fabric détecté.</div>
            <div style={{ marginTop: 4 }}>Ajoutez des workspaces et des items Fabric sur le canvas pour visualiser le lineage.</div>
          </div>
        )}

        {/* Infrastructure summary */}
        {hasFabric && (
          <div style={{ marginBottom: 10, padding: '8px 10px', background: '#F0FDFE', border: '1px solid #038387', borderRadius: 7, fontSize: 11, display: 'flex', gap: 12 }}>
            <span style={{ color: '#0F5256' }}>
              <strong>{fabricCapacities.length}</strong> capacity
            </span>
            <span style={{ color: '#0F5256' }}>
              <strong>{workspaceSections.length}</strong> workspace{workspaceSections.length > 1 ? 's' : ''}
            </span>
            <span style={{ color: '#0F5256' }}>
              <strong>{totalItems}</strong> item{totalItems > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Workspace sections ordered by Medallion layer */}
        {workspaceSections.map((section, idx) => (
          <React.Fragment key={section.wsId}>
            <div style={{
              border: `1.5px solid ${section.layer.border}`,
              borderRadius: 8, marginBottom: 2, overflow: 'hidden',
            }}>
              {/* Workspace header with layer badge */}
              <div style={{
                background: section.layer.bg, padding: '5px 10px',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: section.layer.border, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: section.layer.color, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {section.wsLabel}
                </span>
                <span style={{
                  fontSize: 9, background: section.layer.border, color: 'white',
                  borderRadius: 3, padding: '1px 5px', flexShrink: 0,
                }}>
                  {section.layer.label}
                </span>
                <span style={{ fontSize: 10, color: section.layer.color, opacity: 0.7, flexShrink: 0 }}>
                  {section.items.length} item{section.items.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Items in workspace */}
              <div style={{ padding: '4px 8px 6px' }}>
                {section.items.length === 0 && (
                  <div style={{ fontSize: 10, color: '#9CA3AF', padding: '2px 4px', fontStyle: 'italic' }}>
                    Aucun item
                  </div>
                )}
                {section.items.map(n => {
                  const nodeType: string = n.data?.type || n.data?.resourceType || '';
                  const label: string = n.data?.label || n.data?.params?.name || nodeType;
                  const icon = FABRIC_ICONS[nodeType];
                  return (
                    <div
                      key={n.id}
                      onClick={() => onSelectNode?.(n.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '3px 4px', borderRadius: 5,
                        cursor: onSelectNode ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = section.layer.bg)}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      {icon
                        ? <img src={icon} alt="" style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }} />
                        : <div style={{ width: 16, height: 16, background: section.layer.border, borderRadius: 3, flexShrink: 0 }} />
                      }
                      <span style={{ fontSize: 11, color: '#374151', flex: 1 }}>{label}</span>
                      <span style={{ fontSize: 9, color: '#9CA3AF' }}>{nodeType.replace(/^(fabric_|azurerm_)/, '')}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Flow arrow between workspace sections */}
            {idx < workspaceSections.length - 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '1px 0' }}>
                <FiChevronRight size={14} color="#CBD5E1" style={{ transform: 'rotate(90deg)' }} />
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Legend */}
        {workspaceSections.length > 0 && (
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#F9FAFB', borderRadius: 6, fontSize: 10, color: '#6B7280' }}>
            Les workspaces sont classés par couche Médaillon selon leur nom. Cliquez sur un item pour le sélectionner dans le canvas.
          </div>
        )}
      </div>
    </div>
  );
};

export default FabricLineagePanel;
